// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, ChevronLeft, ChevronRight, Clock, Lock, MapPin, Plus, Pencil, Trash2, Paperclip } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import {
  Badge,
  BottomSheet,
  Button,
  Card,
  Modal,
  ConfirmDialog,
  Input,
  Select,
  Skeleton,
  FileUpload,
} from '@/components/ui'
import { Textarea } from '@/components/ui/Textarea'
import { Alert } from '@/components/ui/Alert'
import { api } from '@/lib/api'
import { translateApiError } from '@/lib/apiError'
import { useAuth } from '@/hooks/useAuth'
import { useUsers } from '@/hooks/useUsers'
import {
  useTimesheetEntries,
  useTimesheetAllUsers,
  useCreateTimesheetEntry,
  useUpdateTimesheetEntry,
  useDeleteTimesheetEntry,
  useLockWeek,
  useUnlockWeek,
} from '@/hooks/useTimesheet'
import type { TimesheetEntry } from '@/hooks/useTimesheet'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { useNotificationStore } from '@/stores/useNotificationStore'
// EP-141 T141-001 — the merged view. useWeekSchedule/TimesheetWeekGrid used
// to belong to the standalone StaffCalendarPage (EP-133 T133-023); the grid
// component moved to components/timesheet/, this hook stayed in hooks/ but
// gained the visitsEnabled gate (T141-001 survey finding (b)).
import { useWeekSchedule } from '@/hooks/useWeekSchedule'
import { TimesheetWeekGrid } from '@/components/timesheet/TimesheetWeekGrid'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useAccountSettings } from '@/hooks/useAccountSettings'

// ─── Date helpers ──────────────────────────────────────────────────────────────

/** Get the Monday of the week that contains `date`. */
function getMondayOf(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay() // 0=Sun, 1=Mon, …, 6=Sat
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

/** Format a Date as YYYY-MM-DD. */
function toISODate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Add `days` days to a Date (returns new Date). */
function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

/**
 * Format a Date as a short display using the account-aware formatter
 * (layout / timezone / Buddhist era) — B-020. The formatter takes an ISO
 * string, so the local Date is normalised to YYYY-MM-DD first.
 */
function formatShortDate(date: Date, formatDate: (d: string | null | undefined) => string): string {
  return formatDate(toISODate(date))
}

/** Format a week as "<Monday> – <Sunday>" via the account-aware formatter. */
function formatWeekRange(monday: Date, formatDate: (d: string | null | undefined) => string): string {
  const sunday = addDays(monday, 6)
  return `${formatDate(toISODate(monday))} – ${formatDate(toISODate(sunday))}`
}

/** Compute hours decimal from HH:MM strings. Returns null if either is missing. */
function computeHours(clockIn: string | null | undefined, clockOut: string | null | undefined): number | null {
  if (!clockIn || !clockOut) return null
  const [inH, inM] = clockIn.split(':').map(Number)
  const [outH, outM] = clockOut.split(':').map(Number)
  const diffMinutes = (outH * 60 + outM) - (inH * 60 + inM)
  if (diffMinutes <= 0) return null
  return Math.round((diffMinutes / 60) * 100) / 100
}

/** Format a date string (YYYY-MM-DD) for display (DD/MM). */
function formatDateDisplay(dateStr: string): string {
  const parts = dateStr.split('-')
  return `${parts[2]}/${parts[1]}`
}

// ─── Upload helper ───────────────────────────────────────────────────────────

async function uploadTimesheetImage(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return `/api/images/${presigned.key}`
}

// ─── Entry form modal ──────────────────────────────────────────────────────────

interface EntryFormData {
  clock_in: string
  clock_out: string
  hours: string
  notes: string
  image_urls: string[]
}

interface EntryModalProps {
  isOpen: boolean
  onClose: () => void
  workDate: string
  entry: TimesheetEntry | null
  onDelete?: (id: number) => void
  isReadOnly: boolean
  // T141-011 — set only when this create originated from someone else's row
  // (admin/owner only; the header Add-shift button never sets these, it
  // always targets the caller). targetUserName is display-only, to make the
  // "whose shift is this" question visible in the title rather than silent —
  // the whole point of this task was fixing a SILENT misattribution, so the
  // form that replaces it should not introduce a new, different silence.
  targetUserId?: number
  targetUserName?: string
  // T141-016 — informational only, never a write gate. The write is already
  // correctly authorized before this modal is even reachable (own_block?/
  // admin_or_higher? in TimesheetBlockPolicy); this exists purely so an
  // admin/owner writing into a locked week is told so, rather than the page
  // staying silent about it the way it did live before this fix.
  targetIsLocked?: boolean
}

function EntryModal({ isOpen, onClose, workDate, entry, onDelete, isReadOnly, targetUserId, targetUserName, targetIsLocked }: EntryModalProps) {
  const { t } = useTranslation('timesheet')
  const formatDate = useDateFormatter()
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)

  const createEntry = useCreateTimesheetEntry()
  const updateEntry = useUpdateTimesheetEntry()

  const [form, setForm] = useState<EntryFormData>({
    clock_in: '',
    clock_out: '',
    hours: '',
    notes: '',
    image_urls: [],
  })

  // Populate form when opening
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (isOpen) {
      setForm({
        clock_in: entry?.clock_in ?? '',
        clock_out: entry?.clock_out ?? '',
        hours: entry?.hours != null ? String(entry.hours) : '',
        notes: entry?.notes ?? '',
        image_urls: entry?.image_urls ?? [],
      })
    }
  }, [isOpen, entry])
  /* eslint-enable react-hooks/set-state-in-effect */

  // Auto-calculate hours when clock_in / clock_out change
  const handleTimeChange = (field: 'clock_in' | 'clock_out', value: string) => {
    const updated = { ...form, [field]: value }
    const auto = computeHours(updated.clock_in, updated.clock_out)
    setForm({ ...updated, hours: auto != null ? String(auto) : updated.hours })
  }

  const handleSubmit = async () => {
    const payload = {
      work_date: workDate,
      clock_in: form.clock_in || null,
      clock_out: form.clock_out || null,
      hours: form.hours ? Number(form.hours) : null,
      notes: form.notes || null,
      image_urls: form.image_urls,
    }

    try {
      if (entry) {
        await updateEntry.mutateAsync({ id: entry.id, ...payload })
        addToast({ type: 'success', message: t('messages.entry_updated') })
      } else {
        // T141-011 — targetUserId rides alongside the entry fields, never
        // inside them; useCreateTimesheetEntry sends it as a top-level
        // sibling of timesheet_entry, matching the backend's params[:user_id].
        await createEntry.mutateAsync(
          targetUserId !== undefined ? { ...payload, user_id: targetUserId } : payload
        )
        addToast({ type: 'success', message: t('messages.entry_created') })
      }
      onClose()
    } catch {
      addToast({ type: 'error', message: tc('errors.something_went_wrong') })
    }
  }

  const isLoading = createEntry.isPending || updateEntry.isPending
  const title = entry
    ? t('edit_entry')
    : targetUserName
      ? t('add_entry_for', { name: targetUserName })
      : t('add_entry')

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        isReadOnly ? (
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.close')}
          </Button>
        ) : (
          <div className="flex gap-2 w-full">
            {entry && onDelete && (
              <Button
                variant="ghost"
                className="text-error mr-auto"
                onClick={() => onDelete(entry.id)}
                disabled={isLoading}
                leftIcon={<Trash2 className="w-4 h-4" />}
              >
                {t('delete_entry')}
              </Button>
            )}
            <Button variant="ghost" onClick={onClose} disabled={isLoading}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={handleSubmit} loading={isLoading}>
              {tc('actions.save')}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        <div className="text-sm text-base-content/60 mb-2">
          {formatDate(workDate)}
        </div>

        {/* T141-016 — only when the target's week is locked AND the write is
            still going through (admin/owner bypass): if isReadOnly is true
            the form really is blocked, and this copy would contradict that. */}
        {targetIsLocked && !isReadOnly && (
          <Alert variant="warning" data-testid="entry-modal-locked-notice">
            {t('locked_week_notice')}
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('clock_in')}
            type="time"
            value={form.clock_in}
            onChange={(e) => handleTimeChange('clock_in', e.target.value)}
            disabled={isReadOnly}
          />
          <Input
            label={t('clock_out')}
            type="time"
            value={form.clock_out}
            onChange={(e) => handleTimeChange('clock_out', e.target.value)}
            disabled={isReadOnly}
          />
        </div>

        <Input
          label={t('hours')}
          type="number"
          step="0.25"
          min="0"
          max="24"
          value={form.hours}
          onChange={(e) => setForm({ ...form, hours: e.target.value })}
          disabled={isReadOnly}
        />

        <Textarea
          label={t('notes')}
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          disabled={isReadOnly}
        />

        <FileUpload
          label={t('attachments')}
          hint={t('attachments_hint')}
          value={form.image_urls}
          onChange={(urls) => setForm({ ...form, image_urls: urls })}
          maxFiles={5}
          accept="image/*"
          onUpload={uploadTimesheetImage}
          disabled={isReadOnly}
        />
      </div>
    </Modal>
  )
}

// ─── Clock proof (EP-154) ───────────────────────────────────────────────────────
//
// The selfie from each tap and the phone's location, on the shift. Shown in
// the entry sheet only (owner, 2026-10-07: the card itself shows neither);
// the admin's review surface, nothing here is editable.

function ClockProof({ entry }: { entry: TimesheetEntry }) {
  const { t } = useTranslation('timesheet')
  // A typed-in entry has no proof at all: nothing to show. A clocked one
  // always shows both columns, the clock-out side as a placeholder while the
  // shift is open (owner, 2026-10-07).
  if (!entry.clock_in_photo_url && !entry.clock_in_location && !entry.clock_out_photo_url && !entry.clock_out_location) return null
  const shots = [
    { key: 'in', url: entry.clock_in_photo_url, loc: entry.clock_in_location, label: t('clock_in'), time: entry.clock_in, pending: false },
    { key: 'out', url: entry.clock_out_photo_url, loc: entry.clock_out_location, label: t('clock_out'), time: entry.clock_out, pending: !!entry.open },
  ]
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="timesheet-clock-proof">
      {shots.map((shot) => (
        <div key={shot.key} className="space-y-1">
          <div className="text-xs text-base-content/60">
            {shot.label}{shot.time ? ` ${shot.time}` : ''}
          </div>
          {shot.url ? (
            <a href={resolveUrl(shot.url)} target="_blank" rel="noopener noreferrer" aria-label={`${shot.label} selfie`}>
              <img src={resolveUrl(shot.url)} alt="" className="w-full aspect-square rounded-lg object-cover" />
            </a>
          ) : (
            <div
              className="w-full aspect-square rounded-lg border border-dashed border-base-300 flex flex-col items-center justify-center gap-1 text-xs text-base-content/40"
              data-testid={`timesheet-proof-placeholder-${shot.key}`}
            >
              <Camera className="w-6 h-6" aria-hidden />
              {shot.pending ? t('not_clocked_out') : t('no_selfie')}
            </div>
          )}
          <div className="text-center text-sm">
            {shot.loc ? (
              <a
                href={`https://www.google.com/maps?q=${shot.loc.lat},${shot.loc.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 link link-primary"
                aria-label={`${shot.label} ${t('location')}`}
              >
                <MapPin className="w-4 h-4" />
                {t('location')}
              </a>
            ) : (
              <span className="text-base-content/40">{shot.pending ? '' : t('no_location')}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Entry sheet (EP-154) ──────────────────────────────────────────────────────
//
// What a tap on a card opens: the shift as it is, its selfies and locations,
// and the edit action for whoever may edit. The card itself stays plain.

function EntrySheet({ entry, canEdit, onClose, onEdit }: {
  entry: TimesheetEntry | null
  canEdit: boolean
  onClose: () => void
  onEdit: (entry: TimesheetEntry) => void
}) {
  const { t } = useTranslation('timesheet')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()
  // A dialog on a desktop, a bottom sheet on a phone (owner, 2026-10-07).
  const isDesktop = useIsDesktop()
  const title = entry ? formatDate(entry.work_date) : ''
  const body = entry && (
    <div className="space-y-4" data-testid="timesheet-entry-sheet">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary/60" />
          <span>{entry.clock_in ?? '–'} – {entry.clock_out ?? '–'}</span>
          {entry.open && <Badge variant="success" size="sm">{t('in_progress')}</Badge>}
        </div>
        {entry.hours != null && <span className="font-semibold text-primary">{entry.hours}h</span>}
      </div>
      <ClockProof entry={entry} />
      {entry.notes && <p className="text-sm text-base-content/70 whitespace-pre-wrap">{entry.notes}</p>}
      {entry.image_urls && entry.image_urls.length > 0 && (
        <div className="flex items-center gap-1 text-sm text-base-content/60">
          <Paperclip className="w-4 h-4" />
          {t('attachments')}: {entry.image_urls.length}
        </div>
      )}
    </div>
  )
  const actions = (
    <>
      <Button variant="ghost" onClick={onClose}>{tc('actions.close')}</Button>
      {canEdit && entry && (
        <Button onClick={() => onEdit(entry)} leftIcon={<Pencil className="w-4 h-4" />} data-testid="timesheet-entry-sheet-edit">
          {t('edit_entry')}
        </Button>
      )}
    </>
  )
  if (isDesktop) {
    return (
      <Modal isOpen={entry !== null} onClose={onClose} title={title} footer={actions}>
        {body}
      </Modal>
    )
  }
  return (
    <BottomSheet isOpen={entry !== null} onClose={onClose} title={title}>
      {body}
      <div className="flex justify-end gap-2 pt-4">{actions}</div>
    </BottomSheet>
  )
}

const API_BASE = import.meta.env.VITE_API_URL || ''
function resolveUrl(url: string): string {
  return url.startsWith('/api/') ? `${API_BASE}${url}` : url
}

// ─── Day cell ──────────────────────────────────────────────────────────────────

interface DayCellProps {
  date: Date
  entries: TimesheetEntry[]
  isLocked: boolean
  canOverride: boolean
  // T141-010: ownership gate (row.isSelf || isAdmin), distinct from the lock
  // gate above. Without this a staff/accountant viewing the all-staff table
  // (their default view since T141-004) saw a live-looking "+" and pencil on
  // every row, including colleagues', that a click would 403 on save — the
  // table only ever gated the LOCK state per row, never ownership, because
  // before this epic only admin (who bypasses both) ever reached this table.
  canEdit: boolean
  onAdd: (workDate: string) => void
  // A tap on a card opens the entry sheet (EP-154); editing starts from there.
  onOpen: (entry: TimesheetEntry, editable: boolean) => void
}

function DayCell({ date, entries, isLocked, canOverride, canEdit, onAdd, onOpen }: DayCellProps) {
  const { t } = useTranslation('timesheet')
  const workDate = toISODate(date)
  const isReadOnly = !canEdit || (isLocked && !canOverride)

  if (entries.length === 0) {
    return (
      <div className="min-h-20 p-2 border border-base-200 rounded-lg bg-base-200 flex items-center justify-center">
        {!isReadOnly && (
          <Button
            variant="ghost"
            size="sm"
            shape="circle"
            className="text-base-content/40 hover:text-primary hover:bg-primary/10"
            onClick={() => onAdd(workDate)}
            aria-label={t('add_entry')}
          >
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-20 p-1.5 border border-primary/20 rounded-lg bg-primary/5 space-y-1">
      {entries.map((entry) => (
        <button
          key={entry.id}
          className="w-full p-1.5 rounded text-left transition-colors bg-base-100/80 hover:bg-primary/10 cursor-pointer"
          onClick={() => onOpen(entry, !isReadOnly)}
          aria-label={`${isReadOnly ? t('view_entry') : t('edit_entry')} - ${formatDateDisplay(workDate)}`}
        >
          <div className="space-y-0.5">
            {(entry.clock_in || entry.clock_out) && (
              <div className="flex items-center gap-1 text-xs">
                <Clock className="w-3 h-3 text-primary/60 shrink-0" />
                <span className="text-base-content/70">
                  {entry.clock_in ?? '–'} – {entry.clock_out ?? '–'}
                </span>
              </div>
            )}
            {entry.open && (
              <div className="text-xs font-semibold text-success" data-testid="timesheet-in-progress">
                {t('in_progress')}
              </div>
            )}
            {entry.hours != null && (
              <div className="text-xs font-semibold text-primary">
                {entry.hours}h
              </div>
            )}
            {entry.notes && (
              <div className="text-xs text-base-content/60 truncate">
                {entry.notes}
              </div>
            )}
            <div className="flex items-center justify-between">
              {entry.image_urls && entry.image_urls.length > 0 ? (
                <div className="flex items-center gap-0.5 text-base-content/40">
                  <Paperclip className="w-2.5 h-2.5" />
                  <span className="text-xs">{entry.image_urls.length}</span>
                </div>
              ) : (
                <div />
              )}
              {!isReadOnly && <Pencil className="w-2.5 h-2.5 text-base-content/30" />}
            </div>
          </div>
        </button>
      ))}
      {!isReadOnly && (
        <button
          className="w-full flex items-center justify-center py-0.5 text-base-content/30 hover:text-primary transition-colors"
          onClick={() => onAdd(workDate)}
          aria-label={t('add_entry')}
        >
          <Plus className="w-3 h-3" />
        </button>
      )}
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────────

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

export function TimesheetPage() {
  const { t } = useTranslation('timesheet')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const formatDate = useDateFormatter()
  const addToast = useNotificationStore((s) => s.addToast)

  const isAdmin = user?.role === 'admin' || user?.role === 'owner'
  const isOwner = user?.role === 'owner'

  // T141-001 (b) — the visitor-management dual gate, mirrored exactly from
  // TodayStrip.tsx / Sidebar.tsx: featureFlags.visitorManagement AND the
  // per-account visitor_management_enabled setting. Passed into
  // useWeekSchedule below so the visits query never fires on an account
  // without the module — without this, every account without it would 403 on
  // /api/visitor_visits on every load and every week-nav once this page is
  // ungated (EP-141 §9 Q-2).
  const featureFlags = useFeatureFlags()
  const { data: accountSettings } = useAccountSettings()
  // The accountant is not in VisitorVisitPolicy#index? (staff and above), so
  // the visits query never fires for that role: with the module on it 403ed on
  // every load and week change (browser pass, 2026-10-17).
  const visitsEnabled =
    featureFlags.visitorManagement &&
    (accountSettings?.settings?.visitor_management_enabled ?? false) &&
    user?.role !== 'accountant'

  // Current week state
  const [currentMonday, setCurrentMonday] = useState<Date>(() => getMondayOf(new Date()))

  // Staff selector (admin/owner only)
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined)

  // Modal state. targetUserId/targetUserName (T141-011) are set only when
  // the add originated from someone else's row (admin/owner only) -- see
  // handleAddEntry below. targetIsLocked (T141-016) is the TARGET's own
  // lock state at the moment the modal opened -- informational only, never
  // a write gate (the write is already correctly gated by canEdit/policy
  // before the modal is even reachable); it exists purely so the page tells
  // an admin/owner they are writing into a locked week instead of staying
  // silent about it, per the design-pass finding.
  // EP-154: the entry sheet a card tap opens; editing starts from it.
  const [entrySheet, setEntrySheet] = useState<{ entry: TimesheetEntry; editable: boolean; rowUserId?: number; rowLocked?: boolean } | null>(null)
  const [entryModal, setEntryModal] = useState<{
    isOpen: boolean
    workDate: string
    entry: TimesheetEntry | null
    targetUserId?: number
    targetUserName?: string
    targetIsLocked?: boolean
  }>({ isOpen: false, workDate: '', entry: null })

  // Confirm dialogs
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean
    entryId: number | null
  }>({ isOpen: false, entryId: null })

  const [lockConfirm, setLockConfirm] = useState(false)
  const [unlockConfirm, setUnlockConfirm] = useState(false)
  // T141-010 -- staff/accountant self-lock, distinct from admin/owner's
  // lockConfirm above (which can target any user via selectedUserId).
  const [selfLockConfirm, setSelfLockConfirm] = useState(false)

  const weekStr = toISODate(currentMonday)

  // Whether viewing all users. T141-004/T141-001: read is open to every
  // role now, so this is no longer isAdmin-gated — staff and accountant
  // default to "all staff" the same as admin/owner, same as the hours table
  // roster below.
  const isAllUsers = !selectedUserId

  // Data — single user mode
  const { data: weekData, isLoading: isLoadingSingle, isFetching: isFetchingSingle } = useTimesheetEntries(weekStr, selectedUserId)
  // Data — all users mode
  const { data: allUsersData, isLoading: isLoadingAll, isFetching: isFetchingAll } = useTimesheetAllUsers(weekStr, isAllUsers)

  const { data: usersData } = useUsers()

  // Data — the WHEN grid (shifts + visits). Filtered to the same selected
  // user as the hours table below, `null` meaning "all staff" — the shared
  // staff filter drives both halves of the page.
  const { shifts, visits } = useWeekSchedule(currentMonday, selectedUserId ?? null, visitsEnabled)

  const isLoading = isAllUsers ? isLoadingAll : isLoadingSingle
  const isFetching = isAllUsers ? isFetchingAll : isFetchingSingle

  const deleteEntry = useDeleteTimesheetEntry()
  const lockWeek = useLockWeek()
  const unlockWeek = useUnlockWeek()

  const entries = weekData?.entries ?? []
  const lock = weekData?.lock ?? null

  // Group entries by work_date (supports multiple entries per day)
  const entriesByDate = new Map<string, TimesheetEntry[]>()
  for (const e of entries) {
    const existing = entriesByDate.get(e.work_date) ?? []
    existing.push(e)
    entriesByDate.set(e.work_date, existing)
  }

  // Whether week is locked and whether current user can override
  const isLocked = lock != null
  const canOverride = isAdmin

  // Build days array (Mon–Sun)
  const days = Array.from({ length: 7 }, (_, i) => addDays(currentMonday, i))

  // Navigation
  const goToPrevWeek = () => setCurrentMonday((d) => addDays(d, -7))
  const goToNextWeek = () => setCurrentMonday((d) => addDays(d, 7))
  const goToThisWeek = () => setCurrentMonday(getMondayOf(new Date()))

  // Staff options for selector. Memoised so the gridStaffColumns memo below
  // (and anything else keyed on `users`) doesn't recompute every render just
  // because `usersData?.data ?? []` allocates a fresh empty-array fallback.
  const users = useMemo(() => usersData?.data ?? [], [usersData])
  const staffOptions = [
    { value: '', label: t('all_staff') },
    ...users.map((u) => ({ value: String(u.id), label: u.name || u.email })),
  ]

  // T141-001 survey finding (a) — the grid's lanes must come from the same
  // account roster the hours table rows from (`users`, above), not from
  // "whoever happens to have a shift this week" (the old StaffCalendarPage
  // derivation). Otherwise a staff member with an empty week gets no lane at
  // all while still appearing as a zero-hours row in the table below.
  const gridStaffColumns = useMemo(() => {
    if (isAllUsers) {
      return users.map((u) => ({ id: u.id, name: u.name || u.email }))
    }
    const targetId = selectedUserId ?? user?.id
    const match = users.find((u) => u.id === targetId)
    if (match) return [{ id: match.id, name: match.name || match.email }]
    return user ? [{ id: user.id, name: user.name || user.email }] : []
  }, [isAllUsers, users, selectedUserId, user])

  // Open add entry modal. targetUserId/targetUserName (T141-011) are set
  // only by a row's own "+" when that row belongs to someone other than the
  // caller (own_block? in TimesheetBlockPolicy already refuses staff/
  // accountant reaching this with a target other than themselves, since
  // canEdit on that row would be false and the row-level "+" would not even
  // render -- see DayCell/canEdit above). targetIsLocked (T141-016) is the
  // target's own lock state, purely informational.
  const handleAddEntry = (workDate: string, targetUserId?: number, targetUserName?: string, targetIsLocked?: boolean) => {
    setEntryModal({ isOpen: true, workDate, entry: null, targetUserId, targetUserName, targetIsLocked })
  }

  // T141-010/T141-011 -- the header "Add shift" action. Always targets the
  // signed-in caller, on every role, even now that POST /api/timesheet_entries
  // can target another user (T141-011). Deliberate, not left over from
  // before the endpoint existed:
  //   - the header button has no row context to draw a target from, only
  //     whatever the shared staff filter (selectedUserId) happens to be set
  //     to for VIEWING purposes right now;
  //   - selectedUserId is a read/view filter by name and by every other use
  //     on this page ("staff_member:", narrows the grid + table). Making it
  //     silently double as a WRITE target would mean an admin who selects a
  //     colleague purely to check whether they logged their hours, then
  //     clicks "Add shift" meaning to log their OWN time, would instead
  //     silently log it for the colleague -- the same shape of silent
  //     misattribution this task exists to fix, reintroduced through a
  //     different door;
  //   - the row-level "+" (wired below) already fully covers "add a shift
  //     for someone else", with the target unambiguous because it is the
  //     exact row clicked. The header button's distinct value is "log my
  //     own time regardless of which view I'm in", which this preserves.
  // Defaults to today if the currently displayed week contains it,
  // otherwise the week's first day.
  const handleHeaderAddShift = () => {
    const today = toISODate(new Date())
    const workDate = days.some((d) => toISODate(d) === today) ? today : toISODate(days[0])
    // T141-016 -- `isLocked` (page-level) already represents the CALLER's
    // own lock state regardless of selectedUserId (see T141-010), which is
    // exactly the target here since this button always targets self.
    setEntryModal({ isOpen: true, workDate, entry: null, targetIsLocked: isLocked })
  }

  // Open edit entry modal. targetIsLocked (T141-016) mirrors handleAddEntry.
  const handleEditEntry = (entry: TimesheetEntry, targetIsLocked?: boolean) => {
    setEntryModal({ isOpen: true, workDate: entry.work_date, entry, targetIsLocked })
  }

  // Request delete (open confirm)
  const handleDeleteRequest = (entryId: number) => {
    setEntryModal({ isOpen: false, workDate: '', entry: null })
    setDeleteConfirm({ isOpen: true, entryId })
  }

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.entryId) return
    try {
      await deleteEntry.mutateAsync(deleteConfirm.entryId)
      addToast({ type: 'success', message: t('messages.entry_deleted') })
    } catch {
      addToast({ type: 'error', message: tc('errors.something_went_wrong') })
    } finally {
      setDeleteConfirm({ isOpen: false, entryId: null })
    }
  }

  // Lock week
  const handleLockConfirm = async () => {
    try {
      await lockWeek.mutateAsync({
        week_start: weekStr,
        user_id: selectedUserId,
      })
      addToast({ type: 'success', message: t('messages.week_locked') })
    } catch (e) {
      // EP-154: the lock can be refused for a week that has not ended, or
      // one with a shift still open in it; say which.
      addToast({ type: 'error', message: translateApiError(e, 'timesheet', { t, tc }) })
    } finally {
      setLockConfirm(false)
    }
  }

  // T141-010 -- staff/accountant self-lock. Always targets the signed-in
  // user explicitly (never selectedUserId, which they may have pointed at a
  // colleague purely to VIEW that colleague's hours) and the currently
  // displayed week. TimesheetLockPolicy#create? grants this to staff
  // unconditionally and to accountant via the T141-004b branch; destroy?
  // (unlock) stays admin_or_higher? only, so this is deliberately one-way
  // for whoever clicks it -- the confirm copy (confirm_self_lock) says so.
  const handleSelfLockConfirm = async () => {
    try {
      await lockWeek.mutateAsync({
        week_start: weekStr,
        user_id: user?.id,
      })
      addToast({ type: 'success', message: t('messages.week_locked') })
    } catch (e) {
      addToast({ type: 'error', message: translateApiError(e, 'timesheet', { t, tc }) })
    } finally {
      setSelfLockConfirm(false)
    }
  }

  // Unlock week
  const handleUnlockConfirm = async () => {
    if (!lock) return
    try {
      await unlockWeek.mutateAsync(lock.id)
      addToast({ type: 'success', message: t('messages.week_unlocked') })
    } catch {
      addToast({ type: 'error', message: tc('errors.something_went_wrong') })
    } finally {
      setUnlockConfirm(false)
    }
  }

  // Format lock date
  const lockDate = lock ? formatDate(lock.created_at) : ''

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title') }]} />

        <PageHeader
          title={t('title')}
          isRefreshing={(isFetching || shifts.isFetching || (visitsEnabled && visits.isFetching)) && !isLoading}
          actions={
            <div className="flex items-center gap-2">
              {/* T141-010 — wired to create a shift for the signed-in
                  operator. Derived from TimesheetBlockPolicy#create?, not
                  from the EP-141 §10.2 mockup text: owner/admin bypass the
                  week-lock entirely for their own shifts (admin_or_higher?
                  returns true unconditionally), so their button is never
                  hidden; staff and accountant may only create their own,
                  unlocked-week shift (own_block? && week_not_locked?), so
                  theirs disappears once their own week is locked rather than
                  opening a form that would 403 on save.

                  This HEADER button always targets the caller. Adding for
                  someone else goes through the per-row "+" instead, which
                  carries an unambiguous target (T141-011). Deliberately not
                  wired to the staff selector: that control is a view filter
                  everywhere else on this page, so letting it double as a
                  write target would let an admin who selected a colleague to
                  check their hours silently log their OWN time against that
                  colleague. */}
              {(isAdmin || !isLocked) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={handleHeaderAddShift}
                  data-testid="calendar-add-shift"
                >
                  {/* T141-017 — owner-chosen vocabulary is "Entry", not
                      "Shift"; reuses the existing add_entry key rather than
                      duplicating the same rendered string under a second one. */}
                  {t('add_entry')}
                </Button>
              )}
              {/* Lock (any user) — admin/owner only. */}
              {isAdmin && !isLocked && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Lock className="w-4 h-4" />}
                  onClick={() => setLockConfirm(true)}
                >
                  {t('lock_week')}
                </Button>
              )}
              {/* T141-010 — self-lock for staff and accountant
                  (TimesheetLockPolicy#create?: staff unconditionally,
                  accountant via the explicit T141-004b branch). Deliberately
                  a separate button and a separate confirm dialog from
                  admin/owner's above — the copy in confirm_self_lock has to
                  say plainly that this is one-way for them (destroy?/unlock
                  stays admin_or_higher? only), which the generic
                  confirm_lock copy does not. */}
              {!isAdmin && !isLocked && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Lock className="w-4 h-4" />}
                  onClick={() => setSelfLockConfirm(true)}
                  data-testid="timesheet-self-lock"
                >
                  {t('lock_my_week')}
                </Button>
              )}
              {isLocked && isOwner && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Lock className="w-4 h-4" />}
                  onClick={() => setUnlockConfirm(true)}
                >
                  {t('unlock_week')}
                </Button>
              )}
            </div>
          }
        />

        {/* Week navigator */}
        <Card>
          {/* Stacks on mobile (nav row, then staff row) so the week range + This
              week button + selector never overflow a phone; desktop keeps one row. */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={goToPrevWeek} aria-label={t('previous_week')} className="shrink-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {/* flex-1 + min-w-0 lets the date range shrink/wrap on mobile instead
                  of forcing the row wider than the viewport. */}
              <span className="font-medium text-sm text-center flex-1 min-w-0 sm:flex-none sm:min-w-40">
                {formatWeekRange(currentMonday, formatDate)}
              </span>
              <Button variant="ghost" size="sm" onClick={goToNextWeek} aria-label={t('next_week')} className="shrink-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToThisWeek} className="shrink-0">
                {t('this_week')}
              </Button>
            </div>

            {/* Staff selector – every role, since T141-004 (owner ruling
                2026-09-01): read is open to everyone, only write/lock stay
                authorized. Drives both the grid above and the table below. */}
            {users.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-base-content/70 whitespace-nowrap">
                  {t('staff_member')}:
                </label>
                <Select
                  value={selectedUserId ? String(selectedUserId) : ''}
                  onChange={(e) =>
                    setSelectedUserId(e.target.value ? Number(e.target.value) : undefined)
                  }
                  options={staffOptions}
                  className="flex-1 min-w-0 sm:flex-none sm:min-w-40"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Lock status banner */}
        {isLocked && lock && (
          <Alert variant="warning" icon={<Lock className="w-4 h-4 shrink-0" />}>
            <span>
              {t('locked_by', {
                name: lock.locked_by?.name ?? '–',
                date: lockDate,
              })}
            </span>
          </Alert>
        )}

        {/* EP-141 T141-001 — the WHEN half. One week selector and one staff
            filter (above) drive this and the hours table below; there is no
            separate view switch (EP-141 §10.1). */}
        <div>
          <h2 className="text-xs text-base-content/50 uppercase mb-2">{t('schedule_label')}</h2>
          <TimesheetWeekGrid
            weekDays={days}
            shifts={shifts.data ?? []}
            visits={visits.data ?? []}
            staffColumns={gridStaffColumns}
            isLoading={shifts.isLoading || (visitsEnabled && visits.isLoading)}
          />
        </div>

        {/* HOW MUCH half. */}
        <div>
          <h2 className="text-xs text-base-content/50 uppercase mb-2">{t('hours_label')}</h2>

          {/* Weekly table */}
          {isLoading ? (
            <Card>
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-20" />
                ))}
              </div>
            </Card>
          ) : (
            <Card>
              {/* Back to all staff button — every role, matching the shared
                  staff filter (T141-004: read is open to everyone). */}
              {selectedUserId && (
                <div className="mb-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<ChevronLeft className="w-4 h-4" />}
                    onClick={() => setSelectedUserId(undefined)}
                  >
                    {t('all_staff')}
                  </Button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th className="min-w-36">{t('staff_member')}</th>
                      {days.map((date, i) => (
                        <th key={i} className="text-center min-w-24">
                          <div className="text-xs text-base-content/50 uppercase">{t(`days.${DAY_KEYS[i]}`)}</div>
                          <div className="text-sm">{formatShortDate(date, formatDate)}</div>
                        </th>
                      ))}
                      {/* Design-pass bug, 2026-09-01: min-w-16 (64px) clipped
                          "Total Hours" to "Total Ho" at desktop width.
                          min-w-28 (112px) plus whitespace-nowrap gives the
                          full label room without wrapping. */}
                      <th className="text-center min-w-28 whitespace-nowrap">{t('total_hours')}</th>
                    </tr>
                  </thead>
                  <tbody className="before:content-[''] before:block before:h-5">
                    {(() => {
                      // Build unified rows: all-users mode or single-user mode.
                      // isSelf compares against the real caller (T141-010) --
                      // it used to be hardcoded false in the all-users branch,
                      // which was harmless while only admin (who bypasses
                      // ownership entirely) ever reached this branch, but
                      // silently made a staff/accountant's OWN row look
                      // non-editable to them once T141-004 made all-staff the
                      // default view for every role.
                      //
                      // Found while wiring T141-016: the single-user branch
                      // had the SAME class of bug the other way round --
                      // hardcoded isSelf: true, which was harmless only while
                      // the staff filter was admin-only (any role picking
                      // themselves), but every role can now select ANY
                      // colleague from that same filter (T141-004), so a
                      // staff member viewing a colleague's single-row view
                      // would have shown a live "+"/pencil on a row that
                      // isn't theirs.
                      const rows = isAllUsers && allUsersData
                        ? allUsersData.users.map((row) => ({
                            userId: row.user.id,
                            name: row.user.name,
                            entries: row.entries,
                            lock: row.lock,
                            isSelf: row.user.id === user?.id,
                          }))
                        : [{
                            userId: weekData?.user?.id ?? user?.id ?? 0,
                            name: weekData?.user?.name ?? user?.name ?? '',
                            entries,
                            lock,
                            isSelf: (weekData?.user?.id ?? user?.id) === user?.id,
                          }]

                      return rows.map((row) => {
                        const rowEntriesByDate = new Map<string, TimesheetEntry[]>()
                        for (const e of row.entries) {
                          const existing = rowEntriesByDate.get(e.work_date) ?? []
                          existing.push(e)
                          rowEntriesByDate.set(e.work_date, existing)
                        }
                        // Design-pass bug, 2026-09-01: `hours` is a decimal
                        // column and Rails serializes decimal as a JSON
                        // STRING ("8.0"), not a number, even though the TS
                        // type says `number | null`. `0 + "8.0"` concatenates
                        // rather than adding, so this rendered "NaNh" for any
                        // row with more than one entry. Number(...) coerces
                        // explicitly rather than trusting the declared type.
                        const rowTotal = row.entries.reduce((sum, e) => sum + Number(e.hours ?? 0), 0)
                        const rowLocked = row.lock != null
                        const rowCanEdit = row.isSelf || isAdmin

                        return (
                          <tr key={row.userId} className={rowLocked ? 'opacity-70' : ''}>
                            <td>
                              <div className="flex items-center gap-2">
                                {rowLocked && <Lock className="w-3 h-3 text-warning shrink-0" />}
                                {isAllUsers ? (
                                  <button
                                    className="font-medium text-sm text-primary hover:underline cursor-pointer"
                                    onClick={() => setSelectedUserId(row.userId)}
                                  >
                                    {row.name}
                                  </button>
                                ) : (
                                  <span className="font-medium text-sm">{row.name}</span>
                                )}
                              </div>
                            </td>
                            {days.map((date, i) => {
                              const workDate = toISODate(date)
                              const dayEntries = rowEntriesByDate.get(workDate) ?? []
                              return (
                                <td key={i} className="p-1 align-top">
                                  <DayCell
                                    date={date}
                                    entries={dayEntries}
                                    isLocked={rowLocked}
                                    canOverride={canOverride}
                                    canEdit={rowCanEdit}
                                    onAdd={(wd) => {
                                      if (isAllUsers) setSelectedUserId(row.userId)
                                      // T141-011 — the row IS the operator's statement of
                                      // intent; send it. undefined for the caller's own
                                      // row keeps the payload byte-identical to before
                                      // (self-only path, no user_id sent at all).
                                      // T141-016 — rowLocked is this row's OWN lock state,
                                      // purely informational for the modal below.
                                      handleAddEntry(wd, row.isSelf ? undefined : row.userId, row.isSelf ? undefined : row.name, rowLocked)
                                    }}
                                    onOpen={(entry, editable) => setEntrySheet({ entry, editable, rowUserId: row.userId, rowLocked })}
                                  />
                                </td>
                              )
                            })}
                            <td className="text-center align-middle">
                              <span className="font-bold text-primary text-sm">
                                {Math.round(rowTotal * 100) / 100}h
                              </span>
                            </td>
                          </tr>
                        )
                      })
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Setup guideline when no entries (single-user mode) */}
              {!isAllUsers && entries.length === 0 && (
                <div className="mt-6 border border-dashed border-base-300 rounded-xl p-6 text-center space-y-3">
                  <Clock className="w-10 h-10 text-base-content/20 mx-auto" />
                  <h3 className="text-base font-semibold text-base-content/70">
                    {t('no_entries')}
                  </h3>
                  <p className="text-sm text-base-content/50 max-w-md mx-auto">
                    {t('setup_guide')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 text-xs text-base-content/40 pt-2">
                    <div className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">1</span>
                      {t('guide_step_1')}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">2</span>
                      {t('guide_step_2')}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">3</span>
                      {t('guide_step_3')}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Entry form modal */}
      <EntrySheet
        entry={entrySheet?.entry ?? null}
        canEdit={entrySheet?.editable ?? false}
        onClose={() => setEntrySheet(null)}
        onEdit={(entry) => {
          const sheet = entrySheet
          setEntrySheet(null)
          if (isAllUsers && sheet?.rowUserId) setSelectedUserId(sheet.rowUserId)
          handleEditEntry(entry, sheet?.rowLocked)
        }}
      />
      <EntryModal
        isOpen={entryModal.isOpen}
        onClose={() => setEntryModal({ isOpen: false, workDate: '', entry: null })}
        workDate={entryModal.workDate}
        entry={entryModal.entry}
        onDelete={handleDeleteRequest}
        isReadOnly={isLocked && !canOverride}
        targetUserId={entryModal.targetUserId}
        targetUserName={entryModal.targetUserName}
        targetIsLocked={entryModal.targetIsLocked}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, entryId: null })}
        onConfirm={handleDeleteConfirm}
        title={t('delete_entry')}
        message={t('confirm_delete')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteEntry.isPending}
      />

      {/* Lock confirm */}
      <ConfirmDialog
        isOpen={lockConfirm}
        onClose={() => setLockConfirm(false)}
        onConfirm={handleLockConfirm}
        title={t('lock_week')}
        message={t('confirm_lock')}
        confirmLabel={t('lock_week')}
        cancelLabel={tc('actions.cancel')}
        variant="warning"
        loading={lockWeek.isPending}
      />

      {/* Unlock confirm */}
      <ConfirmDialog
        isOpen={unlockConfirm}
        onClose={() => setUnlockConfirm(false)}
        onConfirm={handleUnlockConfirm}
        title={t('unlock_week')}
        message={t('confirm_unlock')}
        confirmLabel={t('unlock_week')}
        cancelLabel={tc('actions.cancel')}
        variant="warning"
        loading={unlockWeek.isPending}
      />

      {/* T141-010 — self-lock confirm (staff/accountant). Separate copy from
          confirm_lock: this one has to say plainly that it is one-way for
          the person clicking it (they cannot unlock; only admin/owner can). */}
      <ConfirmDialog
        isOpen={selfLockConfirm}
        onClose={() => setSelfLockConfirm(false)}
        onConfirm={handleSelfLockConfirm}
        title={t('lock_my_week')}
        message={t('confirm_self_lock')}
        confirmLabel={t('lock_my_week')}
        cancelLabel={tc('actions.cancel')}
        variant="warning"
        loading={lockWeek.isPending}
      />
    </>
  )
}
